import type {
  CalendarProviderIntegration,
  ProviderCalendarCatalogItem,
  UserProvider,
} from "@kontextwerk/control-web-client"

export const providerLabel = (provider: UserProvider): string =>
  provider === "google" ? "Google" : "Microsoft"

export const integrationForProvider = (
  integrations: CalendarProviderIntegration[],
  provider: UserProvider,
): CalendarProviderIntegration | null =>
  integrations.find((integration) => integration.provider === provider) ?? null

export const selectedReadCalendarIds = (calendars: ProviderCalendarCatalogItem[]): string[] =>
  calendars
    .filter((calendar) => calendar.can_import_events && calendar.import_enabled)
    .map((calendar) => calendar.provider_calendar_id)

export const selectedWriteCalendarId = (calendars: ProviderCalendarCatalogItem[]): string | null =>
  calendars.find((calendar) => calendar.can_project_events && calendar.projection_enabled)
    ?.provider_calendar_id ?? null

export const calendarDisplayName = (calendar: ProviderCalendarCatalogItem): string =>
  calendar.display_name?.trim() || calendar.provider_calendar_id

export const hasConflictingCalendarProvider = (
  integrations: CalendarProviderIntegration[],
  provider: UserProvider,
): boolean =>
  integrations.some(
    (integration) =>
      integration.provider !== provider &&
      integration.calendar_capability_active &&
      integration.provider_account_id !== null,
  )

export const calendarProviderConflictMessage = (
  integrations: CalendarProviderIntegration[],
  provider: UserProvider,
): string | null => {
  const occupied = integrations.filter(
    (integration) => integration.calendar_capability_active && integration.provider_account_id,
  )

  if (occupied.length > 1)
    return "Für diesen Benutzer sind mehrere Kalenderverbindungen freigegeben. Trennen Sie eine davon, um die Einrichtung fortzusetzen."

  const other = occupied.find((integration) => integration.provider !== provider)

  if (!other) return null

  return other.account_status === "disconnecting"
    ? `Die ${providerLabel(other.provider)}-Kalenderverbindung wird noch getrennt. Bitte warten Sie den Abschluss ab.`
    : `${providerLabel(other.provider)} ist für den Kalender verbunden. Trennen Sie zuerst die ${providerLabel(other.provider)}-Kalenderverbindung.`
}

/** Input supports sanitized rich labels; provider names must remain literal text. */
export const calendarInputLabel = (calendar: ProviderCalendarCatalogItem): string =>
  calendarDisplayName(calendar).replace(
    /[&<>"']/g,
    (character) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character]!,
  )
